import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useEffect, useState } from "react";
import { useGetFilters } from "../../../client/hooks/marketplace/marketplace.queries";
import TuneIcon from "@mui/icons-material/Tune";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

dayjs.locale("pt-br");

export default function TableFilter({ filters, setFilters, refetch }) {
  const [expanded, setExpanded] = useState(true);
  const [canFetch, setCanFetch] = useState(false);

  const { data } = useGetFilters(canFetch);

  useEffect(() => {
    setCanFetch(data ? false : true);
  }, [data]);

  const handleFilterChange = (event, field) => {
    setFilters({ ...filters, [field]: event.target.value });
  };

  const handleDateChange = (newValue, field) => {
    const formattedDate = newValue ? dayjs(newValue).format("DD/MM/YYYY") : "";
    setFilters({ ...filters, [field]: `${formattedDate}` });
  };

  const handleFilter = () => {
    setTimeout(() => refetch(), 100);
  };

  const handleClearFilters = () => {
    setFilters({
      category: "",
      brand: "",
      billingOrigin: "",
      marketplace: "",
      sku: [],
      status: "",
      dateFrom: "",
      dateTo: "",
    });
    setTimeout(() => refetch(), 100);
  };

  const getFilters = [
    {
      name: "category",
      label: "Categoria",
      type: "select",
      options: [...(data?.categories || [])],
    },
    {
      name: "brand",
      label: "Marca",
      type: "select",
      options: [...(data?.brands || [])],
    },
    {
      name: "billingOrigin",
      label: "Origem Faturamento",
      type: "select",
      options: ["Origem 1", "Origem 2", "Origem 3"],
    },
    {
      name: "marketplace",
      label: "Loja",
      type: "select",
      options: data?.marketplaces || [],
    },
    {
      name: "sku",
      label: "SKU",
      type: "text",
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [...(data?.status || [])],
    },
  ];

  const [filterFields, setFilterFields] = useState(getFilters);

  useEffect(() => setFilterFields(getFilters), [data]);

  const handleSkuChange = (event, newValue) => {
    setFilters({ ...filters, sku: newValue });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: "white", // tom neutro suave
          borderRadius: 2,
          mb: 3,
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <Grid
          container
          spacing={2}
          justifyContent={"center"}
          rowGap={2}
          alignItems="center"
        >
          <Grid
            item
            xs={12}
            display="flex"
            alignItems="center"
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: "pointer" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon sx={{ color: "#ff8b00", fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 20 }}>
                Filtros
              </Typography>
            </Box>

            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                marginLeft: 2,
                margin: "0 0 5px 0",
                color: "#ff8b00",
                transition: "color 0.3s ease",
                "&:hover": {
                  color: "#1565c0",
                },
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Grid>

          <Collapse
            in={expanded}
            timeout={300}
            unmountOnExit
            sx={{
              transition: "all 0.3s ease",
              width: "100%",
              padding: "0 10px 10px 10px",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              {filterFields.map((field, index) => {
                // Calcula o valor atual do filtro de forma segura
                const getCurrentValue = () => {
                  const filterValue = filters[field.name];

                  // Se o campo não tem valor, retorna string vazia
                  if (!filterValue && filterValue !== 0) return "";

                  // Se o valor é um objeto, extrai o ID
                  if (typeof filterValue === "object" && filterValue !== null) {
                    const valueId = filterValue?.id;
                    // Verifica se o ID existe nas opções disponíveis
                    const exists = field.options.some(
                      (opt) =>
                        (typeof opt === "object" ? opt.id : opt) === valueId,
                    );
                    return exists ? valueId : "";
                  }

                  // Se o valor é primitivo, verifica se existe nas opções
                  const exists = field.options.some(
                    (opt) =>
                      (typeof opt === "object" ? opt.id : opt) === filterValue,
                  );
                  return exists ? filterValue : "";
                };

                return (
                  <Grid item xs={12} sm={4} md={3} key={index}>
                    {field.type === "select" && (
                      <TextField
                        fullWidth
                        select
                        label={field.label}
                        value={getCurrentValue()}
                        onChange={(e) => handleFilterChange(e, field.name)}
                        variant="outlined"
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#ff8b00",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1e88e5",
                              borderWidth: "2px",
                            },
                          },
                        }}
                      >
                        {field.options.map((option, idx) => (
                          <MenuItem
                            key={option.id || option || idx}
                            value={
                              typeof option === "object" ? option.id : option
                            }
                          >
                            {(typeof option === "object" ? option.name : option)
                              .toLowerCase()
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                    {field.type === "text" && field.name !== "sku" && (
                      <TextField
                        fullWidth
                        label={field.label}
                        value={filters[field.name]}
                        onChange={(e) => handleFilterChange(e, field.name)}
                        variant="outlined"
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: 2,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#ff8b00",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1e88e5",
                              borderWidth: "2px",
                            },
                          },
                        }}
                      />
                    )}

                    {/* Campo SKU com múltiplos valores */}
                    {field.type === "text" && field.name === "sku" && (
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <Autocomplete
                          multiple
                          freeSolo
                          id="tags-filled"
                          value={filters.sku || []}
                          onChange={handleSkuChange}
                          options={[]}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const { key, ...tagProps } = getTagProps({
                                index,
                              });
                              return (
                                <Chip
                                  variant="outlined"
                                  sx={{
                                    height: "22px",
                                    fontSize: "12px",
                                    padding: "0 6px",
                                    ".MuiChip-label": { marginTop: "2px" },
                                    ".MuiSvgIcon-root": { fontSize: "12px" },
                                    marginRight: "6px",
                                  }}
                                  label={option}
                                  key={key}
                                  {...tagProps}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="SKU"
                              placeholder="Digite ou selecione um SKU"
                              sx={{
                                backgroundColor: "#fff",
                                borderRadius: 2,
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#e0e0e0",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#ff8b00",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#1e88e5",
                                    borderWidth: "2px",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </Grid>
                );
              })}

              {/* Campos de data "De" e "Até" */}
              <Grid item xs={12} sm={4} md={3} sx={{ mt: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <DatePicker
                      label="De"
                      format="DD/MM/YYYY"
                      value={
                        filters["dateFrom"]
                          ? dayjs(filters["dateFrom"], "DD/MM/YYYY")
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChange(newValue, "dateFrom")
                      }
                      sx={{
                        ".MuiInputBase-input": {
                          padding: "11px 14px",
                          textAlign: "center",
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          fullWidth
                          inputFormat="DD/MM/YYYY"
                          sx={{
                            backgroundColor: "#fff",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                              "&:hover fieldset": {
                                borderColor: "#ff8b00",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1e88e5",
                                borderWidth: "2px",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <DatePicker
                      label="Até"
                      format="DD/MM/YYYY"
                      value={
                        filters["dateTo"]
                          ? dayjs(filters["dateTo"], "DD/MM/YYYY")
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChange(newValue, "dateTo")
                      }
                      sx={{
                        ".MuiInputBase-input": { padding: "11px 14px" },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          fullWidth
                          inputFormat="DD/MM/YYYY"
                          sx={{
                            backgroundColor: "#fff",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                              "&:hover fieldset": {
                                borderColor: "#ff8b00",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#1e88e5",
                                borderWidth: "2px",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box sx={{ borderTop: "1px solid #eee", mt: 3 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                paddingTop: "20px",
                paddingRight: "10px",
              }}
            >
              <Button
                startIcon={<FilterAltOffIcon />}
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  height: "45px",
                  width: "120px",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  border: "1.5px solid #f28b82", // vermelho claro
                  color: "#d32f2f", // opcional, para o texto
                  lineHeight: "16px",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#fdecea",
                    borderColor: "#e53935", // vermelho mais forte no hover
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                    transition: "transform 0.1s ease-in-out",
                  },
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                }}
              >
                Limpar Filtros
              </Button>

              <Button
                startIcon={<FilterAltIcon sx={{ color: "#4caf50" }} />}
                variant="outlined"
                onClick={handleFilter}
                sx={{
                  color: "#4caf50",
                  borderColor: "#4caf50",
                  height: "45px",
                  width: "120px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 700,
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "#e8f5e9", // fundo verde bem claro
                    borderColor: "#388e3c", // verde mais escuro no hover
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                    transition: "transform 0.1s ease-in-out",
                  },
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                }}
              >
                Filtrar
              </Button>
            </Box>
          </Collapse>

          {/* Botões */}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
